import React, { useState, useEffect } from "react";
import classnames from "classnames";
import Select from "react-select";
import TransitionView from "react-date-picker";
import Calendar from "react-calendar";
import _, { set, update } from "lodash";
import UnknowableNumber from "./unknowableNumber";
import GP5Tree from "./gP5Tree";
import moment from "moment";
import DateTimePicker from "react-datetime-picker";
import {
  QualityAlert,
  QualityAlertLocationInfo,
} from "../../../models/quality-alert";
import { Location } from "../../../models/location";
import { setQuarter } from "date-fns";
import "react-datetime-picker/dist/DateTimePicker.css";
import "react-calendar/dist/Calendar.css";
import "react-clock/dist/Clock.css";

const fromUTCString = (utcString: moment.MomentInput, toJsDate: boolean) => {
  if (!utcString) return;
  const m = moment.utc(utcString, "YYYY-MM-DD HH:mm:ss");
  return m.isValid() ? (toJsDate ? m.local().toDate() : m.local()) : null;
};

const toUTCString = (momentTime: {
  isValid: () => any;
  utc: () => {
    (): any;
    new (): any;
    format: { (arg0: string): any; new (): any };
  };
}) => {
  return momentTime?.isValid()
    ? momentTime.utc().format("YYYY-MM-DD HH:mm:ss")
    : null;
};

interface ProblemDetailsProps {
  locationId?: number;
  qualityAlertId?: number;
  submitted?: boolean;
  className?: string;
  qualityAlert: QualityAlert | null;
  location: Location | null;
  updateQualityAlert: (qualityAlert: QualityAlert) => void;
  setQualityAlertLocationInfo: (
    qualityAlertLocationInfo: QualityAlertLocationInfo,
  ) => void;
  qualityAlertLocationInfo: QualityAlertLocationInfo;
}

const ProblemDetails: React.FC<ProblemDetailsProps> = ({
  locationId,
  qualityAlertId,
  submitted,
  className,
  qualityAlert,
  location,
  updateQualityAlert,
  setQualityAlertLocationInfo,
  qualityAlertLocationInfo,
}) => {
  const [filterGP5, setFilterGP5] = useState("");
  const [invalid, setInvalid] = useState<string[]>([]);
  const [prrNumber, setPrrNumber] = useState<JSX.Element | null>(null);
  const [prrIssued, setPrrIssued] = useState(false);
  const [isBoxChecked, setIsBoxChecked] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());

  useEffect(() => {
    if (qualityAlertId && locationId) {
      // Fetch and set qualityAlert based on qualityAlertId
      // Example: setQualityAlert(apiCallForQualityAlert(qualityAlertId))
    }
  }, [qualityAlertId, locationId]);

  const validate = (qualityAlert: QualityAlert) => {
    let invalidFields = [];
    if (!qualityAlert?.level) invalidFields.push("level");
    if (
      !qualityAlert?.platform ||
      (_.isObject(qualityAlert.platform) && _.isEmpty(qualityAlert.platform))
    )
      invalidFields.push("platform");
    if (!qualityAlert?.gp5) invalidFields.push("gp5");
    if (qualityAlert?.prrIssued && _.isEmpty(qualityAlert?.prrNumber))
      invalidFields.push("prrNumber");
    if (_.isEmpty(qualityAlert?.issueDetail)) invalidFields.push("issueDetail");
    return invalidFields;
  };

  useEffect(() => {
    if (submitted && qualityAlert) setInvalid(validate(qualityAlert));
  }, [submitted, qualityAlert]);

  useEffect(() => {
    if (qualityAlert) {
      setPrrNumber(
        qualityAlert.prrIssued ? (
          <UnknowableNumber
            model={qualityAlert}
            inputClassName={classnames(
              "text-sm w-[100px]",
              invalid.includes("prrNumber") &&
                "border border-red-600 bg-red-600",
            )}
            inputKey="prrNumber"
            invalid={invalid}
            type="text"
            placeholder="SPPS/NC/QR #"
            onChange={(evt) => onNumberChange("prrNumber", evt.target.value)}
            onCheck={(evt: any) =>
              onCheckboxChange("prrNumber", evt.target.checked ? "Unknown" : "")
            }
          />
        ) : null,
      );
    }
  }, [prrIssued]);

  const onDropdownChange = (
    dropdown: string,
    datakey: string | null,
    data: { value: any },
  ) => {
    if (!qualityAlert) return;
    if (dropdown === "prrIssued") {
      if (data.value) {
        qualityAlert.level = 1;
      } else {
        qualityAlert.level = 0;
      }
      qualityAlert.prrIssued = data.value;
      qualityAlertLocationInfo.prrIssued = data.value;
      setPrrIssued(data.value);
    } else if (dropdown === "platform") {
      qualityAlert.platform = data.value.id;
      qualityAlertLocationInfo.platform = data.value.id;
    }

    if (["prrIssued", "sortRequested"].includes(dropdown)) {
      // setQualityAlert((prevState) => ({
      //   ...prevState,
      //   level: newValue ? "1" : "0",
      // }));
    }
    setQualityAlertLocationInfo({ ...qualityAlertLocationInfo });
    updateQualityAlert({ ...qualityAlert });
  };

  const onDateChange = (date: Date | null) => {
    if (!date || !qualityAlert) return;
    setSelectedDate(date);
    qualityAlert.time = date;
    qualityAlertLocationInfo.time = date;
    setQualityAlertLocationInfo({ ...qualityAlertLocationInfo });
    updateQualityAlert({ ...qualityAlert });
  };

  const onCheckboxChange = (property: string, value: string | null) => {
    if (!qualityAlert) return;
    if (qualityAlert.prrNumber === "Unknown") {
      qualityAlert.prrNumber = "";
      qualityAlertLocationInfo.prrNumber = "";
      setIsBoxChecked(false);
    } else {
      qualityAlert.prrNumber = "Unknown";
      qualityAlertLocationInfo.prrNumber = "Unknown";
      setIsBoxChecked(true);
    }

    setQualityAlertLocationInfo({ ...qualityAlertLocationInfo });
    updateQualityAlert({ ...qualityAlert });
  };

  const onNumberChange = (property: string, value: string) => {
    if (!qualityAlert) return;
    if (property === "supplierIssueNumber") {
      qualityAlertLocationInfo.supplierIssueNumber = value;
    } else if (property === "prrNumber") {
      qualityAlert.prrNumber = value;
      qualityAlertLocationInfo.prrNumber = value;
      updateQualityAlert({ ...qualityAlert });
    }
    setQualityAlertLocationInfo({ ...qualityAlertLocationInfo });
  };

  const onAddPlatformClick = async () => {
    // const id = await actionsLocations.createPlatform(locationId);
    // const modal = {
    //   id: "PlatformsAdd",
    //   props: {
    //     title: "New Platform",
    //     locationId,
    //     index: id,
    //     onSuccess: onFinishAddPlatform,
    //   },
    // };
    // actions.showModal(modal);
  };

  const onFinishAddPlatform = (platform: any) => {
    if (!location) return;
    const matchingPlatform = _.find(location.platforms, {
      ...platform,
      active: true,
    });
    //if (matchingPlatform) onDropdownChange("platform", "id", matchingPlatform);
  };

  const updateQualityAlertIssueDetail = (evt: any) => {
    if (!qualityAlert) return;
    qualityAlert.issueDetail = evt.target.value;
    updateQualityAlert({ ...qualityAlert });
  };

  return (
    <div className={classnames(className, "w-full")}>
      <div className="grid grid-cols-[repeat(auto-fit,_minmax(500px,_1fr))] gap-4">
        <div className="flex flex-col items-start rounded-lg bg-gray-300 p-4">
          <div className="grid grid-cols-[1fr_3fr] items-center gap-4">
            <div className="my-2 flex flex-col items-center justify-self-end">
              <div className="ml-2 mr-5 text-lg italic text-black">
                <div className="flex items-center text-right">
                  <div className="h-[15px] w-[15px] bg-no-repeat" />{" "}
                  {/* Calendar icon */}
                  <p className="ml-2 max-w-[120px] text-lg font-medium text-black">
                    Date & Time Detected
                  </p>
                </div>
              </div>
            </div>
            <DateTimePicker
              className="bg-white text-black"
              onChange={onDateChange}
              value={selectedDate}
              format="y-MM-dd hh:mm a"
              maxDate={new Date()}
            ></DateTimePicker>
          </div>

          <div className="grid grid-cols-[1fr_3fr] items-center gap-4">
            <div className="my-2 flex flex-col items-center justify-self-end">
              <div className="ml-2 mr-5 text-lg italic text-black">
                <div className="flex items-center text-right">
                  <div className="h-[15px] w-[15px] bg-no-repeat" />{" "}
                  {/* Lighting icon */}
                  <p className="ml-2 max-w-[120px] text-lg font-medium text-black">
                    OEM Official Complaint
                  </p>
                </div>
              </div>
            </div>
            <div className="flex items-center justify-start">
              <Select
                className="rounded-md border border-gray-300 bg-white p-2 text-black"
                options={[
                  { label: "Yes", value: true },
                  { label: "No", value: false },
                ]}
                onChange={(selectedOption) =>
                  onDropdownChange("prrIssued", null, {
                    value: selectedOption?.value,
                  })
                }
              />
              {prrNumber}
            </div>
            <div className="my-2 flex flex-col items-center justify-self-end">
              <div className="ml-2 mr-5 text-lg italic text-black">
                <div className="flex items-center text-right">
                  <div className="h-[15px] w-[15px] bg-no-repeat" />{" "}
                  {/* Lighting icon */}
                  <p className="ml-2 max-w-[120px] text-lg font-medium text-black">
                    Customer Issued QC #
                  </p>
                </div>
              </div>
            </div>
            <UnknowableNumber
              inputClassName="w-62 h-9 rounded border border-gray-300 bg-white text-sm"
              model={qualityAlert}
              inputKey={"supplierIssueNumber"}
              invalid={invalid}
              type={"text"}
              placeholder={"Customer Issued QC #"}
              onChange={(evt) =>
                onNumberChange("supplierIssueNumber", evt.target.value)
              }
              onCheck={(evt: any) =>
                onCheckboxChange(
                  "supplierIssueNumber",
                  evt.target.checked ? "Unknown" : "",
                )
              }
            />
            {/* <div className="flex items-center justify-start">
              <textarea
                className={classnames(
                  "h-10 rounded-md border border-gray-300 bg-white p-2 text-black",
                  invalid.includes("platform") && "border-red-600 bg-red-600",
                )}
                value={qualityAlertLocationInfo?.supplierIssueNumber}
                onChange={updateQualityAlertSupplierIssueNumber}
              />
            </div> */}
            <div className="my-2 flex flex-col items-center justify-self-end">
              <div className="ml-2 mr-5 text-lg italic text-black">
                <div className="flex items-center text-right">
                  <div className="h-[15px] w-[15px] bg-no-repeat" />{" "}
                  {/* Lighting icon */}
                  <p className="ml-2 max-w-[120px] text-lg font-medium text-black">
                    Level
                  </p>
                </div>
              </div>
            </div>
            {qualityAlert?.level === 1 ? (
              <p className="flex max-w-max items-center rounded bg-red-500 p-1.5">
                Quality Alert
              </p>
            ) : (
              <p className="flex max-w-max items-center rounded bg-orange-500 p-1.5">
                Near Miss
              </p>
            )}
            <div className="my-2 flex flex-col items-center justify-self-end">
              <div className="ml-2 mr-5 text-lg italic text-black">
                <div className="flex items-center text-right">
                  <div className="h-[15px] w-[15px] bg-no-repeat" />{" "}
                  {/* Lighting icon */}
                  <p className="ml-2 max-w-[120px] text-lg font-medium text-black">
                    Platforms
                  </p>
                </div>
              </div>
            </div>
            <div className="flex items-center justify-start">
              <Select
                className="rounded-md border border-gray-300 bg-white p-2 text-black"
                options={location?.platforms}
                getOptionLabel={(option) => option.desc}
                onChange={(selectedOption) =>
                  onDropdownChange("platform", null, {
                    value: selectedOption,
                  })
                }
              />
            </div>
            <div className="my-2 flex flex-col items-center justify-self-end">
              <div className="ml-2 mr-5 text-lg italic text-black">
                <div className="flex items-center text-right">
                  <div className="h-[15px] w-[15px] bg-no-repeat" />{" "}
                  {/* Lighting icon */}
                  <p className="ml-2 max-w-[120px] text-lg font-medium text-black">
                    Issue Detail
                  </p>
                </div>
              </div>
            </div>
            <div>
              <textarea
                className={classnames(
                  "rounded-md border border-gray-300 bg-white p-2 text-black",
                  invalid.includes("issueDetail") &&
                    "border-red-600 bg-red-600",
                )}
                value={qualityAlert?.issueDetail}
                onChange={updateQualityAlertIssueDetail}
              />
            </div>
          </div>

          <div className="col-span-full flex flex-grow flex-col items-start justify-evenly rounded-md p-2">
            <div className="text-lg italic text-black">ISSUE SUMMARY</div>
            <input
              className="m-0 w-[300px] rounded-md border border-gray-400 bg-white p-2 text-sm text-black"
              type="search"
              value={filterGP5}
              onChange={(evt) => setFilterGP5(evt.target.value)}
              placeholder="search..."
            />
            <GP5Tree
              filter={filterGP5}
              className={classnames(
                invalid.includes("gp5") && "border-red-600 bg-red-600",
              )}
              selectedLeaf={qualityAlert?.gp5}
              onSelectLeaf={updateQualityAlert}
              qualityAlert={qualityAlert}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProblemDetails;
